import { ClipboardList, FileDown } from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { EmptyState, ErrorState, LoadingState } from '@/components/feedback/states';
import { QualitySheetDocument } from '@/features/quality/QualitySheetDocument';
import { emitirPdfDaFicha } from '@/features/quality/quality-sheet-pdf';
import { useContainer, useSessionState } from '@/features/session/use-session';
import { useLive } from '@/lib/use-live';

/** O mesmo `<select>` das outras telas — o componente nativo, com a altura do toque. */
const SELECT_CLASS =
  'h-11 w-full rounded-md border border-(--color-border-subtle) bg-(--color-surface) md:h-9 px-2 text-sm';

/**
 * Qualidade por serviço — a FVS do serviço, pavimento por pavimento (Fase 24, RN-298 e RN-299).
 *
 * Escolhe-se o serviço da obra aberta e, se ele estiver em mais de uma checklist, qual delas.
 * A ficha é **derivada** do que já existe — as execuções conferidas, as verificações e as
 * NCs —, e nada se grava aqui: quem verifica continua verificando na medição do dia, e esta
 * tela é onde se vê o serviço inteiro de uma vez, e se leva o papel para a obra.
 *
 * Só leitura. A permissão da tela é `page.quality`; a da leitura, `employees.view` na obra —
 * a mesma da conferência (ADR-155).
 */
export function QualitySheetPage() {
  const container = useContainer();
  const { session, activeProjectId } = useSessionState();

  // A escolha vale para a obra em que foi feita: trocar de obra no seletor do topo a
  // descarta, porque o serviço escolhido pode nem existir na outra.
  const [choice, setChoice] = useState<{
    projectId: string;
    serviceId: string;
    checklistId: string;
  }>({ projectId: '', serviceId: '', checklistId: '' });
  const [generating, setGenerating] = useState(false);

  const options = useLive(
    () =>
      session === undefined || activeProjectId === undefined
        ? Promise.resolve(undefined)
        : container.listQualitySheetServices.execute({ projectId: activeProjectId }, session),
    [session?.userId, activeProjectId],
  );

  const serviceId = choice.projectId === activeProjectId ? choice.serviceId : '';
  const chosen = useMemo(
    () =>
      options !== undefined && options.ok
        ? options.value.find((option) => option.serviceId === serviceId)
        : undefined,
    [options, serviceId],
  );
  // Uma ficha só não é escolha de ninguém; mais de uma, a pessoa decide.
  const checklistId =
    chosen === undefined
      ? ''
      : chosen.checklists.length === 1
        ? chosen.checklists[0]!.id
        : chosen.checklists.some((checklist) => checklist.id === choice.checklistId)
          ? choice.checklistId
          : '';

  const view = useLive(
    () =>
      session === undefined ||
      activeProjectId === undefined ||
      chosen === undefined ||
      checklistId === ''
        ? Promise.resolve(undefined)
        : container.getServiceQualitySheet.execute(
            { projectId: activeProjectId, serviceId: chosen.serviceId, checklistId },
            session,
          ),
    [session?.userId, activeProjectId, chosen?.serviceId, checklistId],
  );

  if (activeProjectId === undefined) {
    return (
      <EmptyState
        title="Nenhuma obra selecionada"
        description="Escolha uma obra no seletor do topo para ver a qualidade dos serviços."
        icon={<ClipboardList className="size-8" />}
      />
    );
  }

  if (options === undefined) return <LoadingState label="Carregando os serviços…" />;
  if (!options.ok) return <ErrorState message={options.error.message} code={options.error.code} />;

  const emitir = async () => {
    if (view === undefined || !view.ok) return;
    setGenerating(true);
    try {
      await emitirPdfDaFicha(view.value);
    } catch (causa) {
      console.error('[obraplan] falha ao gerar o PDF da ficha de qualidade', causa);
      toast.error('Não foi possível gerar o PDF desta ficha.');
    } finally {
      setGenerating(false);
    }
  };

  // O select agrupa por disciplina: é como o catálogo se lê, e são mais de cem serviços.
  const porDisciplina = new Map<string, typeof options.value>();
  for (const option of options.value) {
    porDisciplina.set(option.disciplineName, [
      ...(porDisciplina.get(option.disciplineName) ?? []),
      option,
    ]);
  }

  return (
    <div className="flex flex-col gap-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Qualidade por serviço</h1>
          <p className="text-(--color-content-muted)">
            A ficha de verificação (FVS) do serviço, pavimento por pavimento: o que foi executado e
            conferido, o que foi aprovado ou reprovado, e o que ainda falta inspecionar.
          </p>
        </div>
        <Button
          variant="secondary"
          disabled={view === undefined || !view.ok || generating}
          onClick={() => void emitir()}
        >
          <FileDown aria-hidden="true" />
          {generating ? 'Gerando o PDF…' : 'Baixar o PDF'}
        </Button>
      </header>

      {options.value.length === 0 ? (
        <EmptyState
          title="Nenhum serviço desta obra tem checklist"
          description="A ficha existe para o serviço que tem checklist. Ligue uma em Configurações → Checklists, no campo Aplica-se a."
          icon={<ClipboardList className="size-8" />}
        />
      ) : (
        <>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="flex flex-col gap-1.5">
              <label htmlFor="quality-service" className="text-sm font-medium">
                Serviço
              </label>
              <select
                id="quality-service"
                className={SELECT_CLASS}
                value={serviceId}
                onChange={(event) =>
                  setChoice({
                    projectId: activeProjectId,
                    serviceId: event.target.value,
                    checklistId: '',
                  })
                }
              >
                <option value="">Escolha o serviço</option>
                {[...porDisciplina].map(([disciplina, servicos]) => (
                  <optgroup key={disciplina} label={disciplina}>
                    {servicos.map((servico) => (
                      <option key={servico.serviceId} value={servico.serviceId}>
                        {servico.name}
                        {servico.isActive ? '' : ' (desativado)'}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </div>

            {chosen !== undefined && chosen.checklists.length > 1 && (
              <div className="flex flex-col gap-1.5">
                <label htmlFor="quality-checklist" className="text-sm font-medium">
                  Checklist
                </label>
                <select
                  id="quality-checklist"
                  className={SELECT_CLASS}
                  value={checklistId}
                  onChange={(event) =>
                    setChoice({
                      projectId: activeProjectId,
                      serviceId,
                      checklistId: event.target.value,
                    })
                  }
                >
                  <option value="">Este serviço está em mais de uma — escolha</option>
                  {chosen.checklists.map((checklist) => (
                    <option key={checklist.id} value={checklist.id}>
                      {checklist.code === undefined
                        ? checklist.name
                        : `${checklist.code} · ${checklist.name}`}
                      {checklist.isActive ? '' : ' (desativada)'}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {chosen === undefined ? (
            <EmptyState
              title="Escolha um serviço"
              description="A ficha mostra os pavimentos em que o serviço foi executado e a conferência do dia foi encerrada."
              icon={<ClipboardList className="size-8" />}
            />
          ) : checklistId === '' ? (
            <EmptyState
              title="Escolha a checklist"
              description="Este serviço é verificado por mais de uma ficha. Cada uma tem a sua grade."
              icon={<ClipboardList className="size-8" />}
            />
          ) : view === undefined ? (
            <LoadingState label="Montando a ficha…" />
          ) : !view.ok ? (
            <ErrorState message={view.error.message} code={view.error.code} />
          ) : (
            <QualitySheetDocument view={view.value} />
          )}
        </>
      )}
    </div>
  );
}
